import Button from "@components/Button";
import Icon from "@components/Icon";
import { Input } from "@components/Input";
import { Select } from "@components/Select";
import { LOGIN } from "@constants/internal-route.constant";
import { AuthHead, AuthLinks } from "@layout/AuthHead";
import { AuthService } from "@services/api/Auth.service";
import { ToastService } from "@services/utils/toast.service";
import { ENV } from "config/ENV.config";
import { G_CAPTCHA_V3_SITE_KEY } from "config/constants";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";

declare const window: any;

const SignUp = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [regSuccess, setRegSuccess] = useState<boolean>(false);
  const [countries, setCountries] = useState<[]>([]);
  const [captchaToken, setCaptchaToken] = useState<any>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    AuthService.getCountries()
      .then((res) => {
        setCountries(res?.body);
      })
      .catch((err) => console.log(err?.msg))
      .finally(() => {
        setTimeout(() => {
          loadGoogleReCaptchaV3();
        }, 2500);

        setIsLoading(false);
      });
  }, []);

  const onSubmit = (fomrData) => {
    if (!captchaToken) {
      ToastService.warning("reCaptcha is not valid");
    }
    const productCode = ENV.productCode;

    setIsLoading(true);

    fomrData["productCode"] = ENV.productCode;
    fomrData["captchaValue"] = captchaToken;
    fomrData["appName"] = productCode || "";

    AuthService.signUp(fomrData)
      .then((resp) => {
        ToastService.success(resp?.msg);
        setRegSuccess(true);
      })
      .catch((err) => ToastService.error(err?.msg))
      .finally(() => {
        setIsLoading(false);
        refreshReCaptchaToken();
      });
  };

  const loadGoogleReCaptchaV3 = () => {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.google.com/recaptcha/api.js?render=${G_CAPTCHA_V3_SITE_KEY}`;
    script.addEventListener("load", () => {
      refreshReCaptchaToken();
    });
    document.body.appendChild(script);
  };

  const refreshReCaptchaToken = () => {
    window.grecaptcha.ready(() => {
      window.grecaptcha.execute(G_CAPTCHA_V3_SITE_KEY, { action: "register" }).then((token: string) => {
        //localStorage.setItem(RECAPTCHA_TOKEN_KEY, token)
        setCaptchaToken(token);
      });
    });
  };

  return (
    <>
      <AuthHead title="Create your account">
        {`It only takes a minute — try ${ENV.brandName} free for 30 days, no card needed.`}
      </AuthHead>
      {regSuccess ? (
        <div className="alert alert-success" role="alert">
          Your registration has been completed successfully.{" "}
          <b>Please check your email to verify your account. It may take maximum 10 minutes</b> to the email.
        </div>
      ) : (
        <></>
      )}
      <form noValidate onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-md-12">
            <Input
              label="Business Name"
              registerProperty={{
                ...register("bizName", {
                  required: "Please provide a business name",
                }),
              }}
              isError={!!errors?.bizName}
              errorMessage={errors?.bizName?.message as string}
            />
          </div>
          <div className="col-md-12">
            <Input
              label="Full Name"
              registerProperty={{
                ...register("fullName", { required: "Name is required" }),
              }}
              isError={!!errors?.fullName}
              errorMessage={errors?.fullName?.message as string}
            />
          </div>
          <div className="col-md-12">
            <Input
              label="Email Address"
              registerProperty={{
                ...register("email", { required: "Provide an email" }),
              }}
              isError={!!errors?.email}
              errorMessage={errors?.email?.message as string}
            />
          </div>
          <div className="col-md-12">
            <Input
              label="Phone Number"
              type="number"
              registerProperty={{
                ...register("mobile", {
                  required: "Please provide phone number",
                }),
              }}
              isError={!!errors?.mobile}
              errorMessage={errors?.mobile?.message as string}
            />
          </div>
          <div className="col-sm-12 col-md-6">
            <Input
              label="Password"
              type="password"
              registerProperty={{
                ...register("pwdKey", {
                  required: "Please provide a strong password",
                }),
              }}
              isError={!!errors?.pwdKey}
              errorMessage={errors?.pwdKey?.message as string}
            />
          </div>
          <div className="col-sm-12 col-md-6">
            <Input
              label="Conf. Password"
              type="password"
              registerProperty={{
                ...register("confPass", {
                  required: "Confirm password is missing",
                }),
              }}
              isError={!!errors?.confPass}
              errorMessage={errors?.confPass?.message as string}
            />
          </div>
          <div className="col-sm-12 col-md-6">
            <Select
              label="Country"
              isRequired
              options={countries}
              textKey="title"
              valueKey="id"
              registerProperty={{
                ...register("countryId", { required: "Country is required" }),
              }}
              isError={!!errors?.countryId}
              errorMessage={errors?.countryId?.message as string}
            />
          </div>
        </div>

        <div className="mt-2">
          <Button
            className="w-100"
            color="primary"
            type="submit"
            size="lg"
            isDisabled={isLoading}
            loadingText="Creating account..."
          >
            <Icon icon="rocket-takeoff" size={18} /> Create account
          </Button>
        </div>

        <AuthLinks>
          <span>
            Already have an account? <Link to={LOGIN}>Sign in</Link>
          </span>
        </AuthLinks>
      </form>
    </>
  );
};

export default SignUp;
